import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../../components/layout/AppLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { apiFetch } from '../../lib/api-client'
import { t } from '../../lib/i18n'

type PartyKind = 'person' | 'selskab'

interface RenterCreateBody {
  name: string
  email: string
  phone?: string
  cpr?: string
  party_kind: PartyKind
  cvr_nr?: string
}

export function RenterCreatePage() {
  const strings = t()
  const navigate = useNavigate()

  const [partyKind, setPartyKind] = useState<PartyKind>('person')
  // Danish-CVR toggle: on → 8-digit CVR + cvrapi.dk lookup; off → foreign
  // selskab with a free-text registration number, no lookup.
  const [hasDanishCvr, setHasDanishCvr] = useState(true)
  const [form, setForm] = useState({ name: '', email: '', phone: '', cpr: '', cvr_nr: '' })
  const [errors, setErrors] = useState<Partial<typeof form>>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [cvrLoading, setCvrLoading] = useState(false)

  const isSelskab = partyKind === 'selskab'

  const validate = (): boolean => {
    const next: Partial<typeof form> = {}
    if (!form.name.trim()) next.name = strings.auth.required
    if (!form.email.trim()) next.email = strings.auth.required
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = strings.auth.invalidEmail
    if (isSelskab) {
      if (hasDanishCvr) {
        if (!/^\d{8}$/.test(form.cvr_nr.replace(/\s/g, ''))) next.cvr_nr = strings.renters.cvrInvalid
      } else if (!form.cvr_nr.trim()) {
        next.cvr_nr = strings.auth.required
      }
    } else if (form.cpr.trim()) {
      const digits = form.cpr.replace('-', '')
      if (!/^\d{10}$/.test(digits)) next.cpr = 'CPR-nummeret skal bestå af 10 cifre'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleCvrFetch = async () => {
    const cvr = form.cvr_nr.replace(/\s/g, '')
    if (!/^\d{8}$/.test(cvr)) {
      setErrors((e) => ({ ...e, cvr_nr: strings.renters.cvrInvalid }))
      return
    }
    setCvrLoading(true)
    setErrors((e) => ({ ...e, cvr_nr: undefined }))
    try {
      const data = await apiFetch<{ name: string; address: string; phone: string; email: string; cvr: string }>(
        `/api/v1/companies/cvr-lookup/${cvr}`,
      )
      // Autofill the whole party from the registry — name, phone and email.
      // (Address has no field on this form yet; it is captured in the wizard.)
      setForm((f) => ({
        ...f,
        name: data.name || f.name,
        phone: data.phone || f.phone,
        email: data.email || f.email,
        cvr_nr: data.cvr || f.cvr_nr,
      }))
    } catch {
      setErrors((e) => ({ ...e, cvr_nr: strings.renters.cvrInvalid }))
    } finally {
      setCvrLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    setServerError(null)

    const body: RenterCreateBody = {
      name: form.name.trim(),
      email: form.email.trim(),
      party_kind: partyKind,
    }
    if (form.phone.trim()) body.phone = form.phone.trim()
    if (isSelskab) {
      // Danish CVR is whitespace-stripped 8 digits; a foreign reg number is
      // stored verbatim (trimmed) in the same cvr_nr column.
      body.cvr_nr = hasDanishCvr ? form.cvr_nr.replace(/\s/g, '') : form.cvr_nr.trim()
    } else if (form.cpr.trim()) {
      body.cpr = form.cpr.replace('-', '')
    }

    try {
      await apiFetch('/api/v1/renters', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      navigate('/lejere')
    } catch {
      setServerError(strings.renters.createError)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AppLayout>
      <div style={{ maxWidth: 520 }}>
        <Card padding="md">
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 20, marginTop: 0 }}>{strings.renters.create}</h2>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 8 }}>
                {strings.renters.partyKind}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <PillButton active={!isSelskab} onClick={() => setPartyKind('person')}>{strings.renters.partyPerson}</PillButton>
                <PillButton active={isSelskab} onClick={() => setPartyKind('selskab')}>{strings.renters.partySelskab}</PillButton>
              </div>
            </div>

            {isSelskab && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 8 }}>
                    {strings.renters.hasDanishCvr}
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <PillButton active={hasDanishCvr} onClick={() => setHasDanishCvr(true)}>{strings.general.yes}</PillButton>
                    <PillButton active={!hasDanishCvr} onClick={() => setHasDanishCvr(false)}>{strings.general.no}</PillButton>
                  </div>
                  <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{strings.renters.hasDanishCvrHelp}</p>
                </div>

                {hasDanishCvr ? (
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
                      {strings.renters.cvr}
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Input
                          placeholder={strings.renters.cvrPlaceholder}
                          value={form.cvr_nr}
                          onChange={(e) => setForm({ ...form, cvr_nr: e.target.value })}
                          error={errors.cvr_nr}
                          autoFocus
                        />
                      </div>
                      <Button type="button" variant="secondary" onClick={handleCvrFetch} isLoading={cvrLoading}>
                        {strings.renters.cvrLookup}
                      </Button>
                    </div>
                    <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{strings.renters.cvrHelp}</p>
                  </div>
                ) : (
                  <Input
                    label={strings.renters.foreignReg}
                    placeholder={strings.renters.foreignRegPlaceholder}
                    value={form.cvr_nr}
                    onChange={(e) => setForm({ ...form, cvr_nr: e.target.value })}
                    error={errors.cvr_nr}
                  />
                )}
              </>
            )}

            <Input
              label={strings.renters.name}
              placeholder={strings.renters.namePlaceholder}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              error={errors.name}
            />

            <Input
              label={strings.renters.email}
              type="email"
              placeholder={strings.renters.emailPlaceholder}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              error={errors.email}
            />

            <Input
              label={strings.renters.phone}
              type="tel"
              placeholder={strings.renters.phonePlaceholder}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              error={errors.phone}
            />

            {!isSelskab && (
              <div>
                <Input
                  label={strings.renters.cpr}
                  type="password"
                  placeholder={strings.renters.cprPlaceholder}
                  value={form.cpr}
                  onChange={(e) => setForm({ ...form, cpr: e.target.value })}
                  error={errors.cpr}
                  autoComplete="off"
                />
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{strings.renters.cprHelp}</p>
              </div>
            )}

            {serverError && (
              <p style={{ fontSize: 12, color: '#dc2626' }}>{serverError}</p>
            )}

            <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
              <Button type="submit" isLoading={submitting}>
                {strings.general.create}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/lejere')}
              >
                {strings.general.cancel}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </AppLayout>
  )
}

function PillButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '6px 16px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 500,
        border: `1px solid ${active ? '#4f46e5' : '#e8ecf4'}`,
        background: active ? '#eef2ff' : hovered ? '#f8fafc' : '#ffffff',
        color: active ? '#4f46e5' : '#64748b',
        cursor: 'pointer',
        transition: 'all 0.12s',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  )
}
